import { useState } from "react";

import { Loader2, LogOut } from "lucide-react";

import { Button } from "src/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from "src/components/ui/dialog";

export function DangerZoneSection({
  store,
  deleteIntegration,
  leaveIntegration,
  loading,
  canDelete,
  canLeave,
  dataSyncSection,
  showLeave,
}) {
  const [deleteStoreOpen, setDeleteStoreOpen] = useState(false);
  const [leaveStoreOpen, setLeaveStoreOpen] = useState(false);

  const handleDeleteStore = async () => {
    await deleteIntegration({
      id: store.id,
    });
    setDeleteStoreOpen(false);
  };

  const handleLeaveStore = async () => {
    await leaveIntegration({
      id: store.id,
    });
    setLeaveStoreOpen(false);
  };

  return (
    <>
      <div>
        <h3 className="mb-4 text-lg font-medium">Danger Zone</h3>
        <div className="space-y-3">
          {dataSyncSection}

          {showLeave && (
            <div className="border-border flex flex-col items-start justify-between gap-4 rounded-md border p-4 sm:flex-row sm:items-center">
              <div className="text-foreground flex w-full flex-col gap-2">
                <span className="font-medium">Leave this store</span>
                <div className="text-muted-foreground text-sm">
                  {canLeave
                    ? "Remove this store from your account. Store data and other team members are not affected."
                    : "You cannot leave this store while you are the billing user. Transfer billing to another admin before leaving."}
                </div>
              </div>
              <div className="flex h-full w-full items-end justify-end sm:w-fit">
                <Button
                  variant="outline"
                  disabled={!canLeave}
                  onClick={() => setLeaveStoreOpen(true)}
                >
                  <LogOut className="size-4" />
                  Leave Store
                </Button>
              </div>
            </div>
          )}

          {canDelete && (
            <div className="border-destructive flex flex-col items-start justify-between gap-4 rounded-md border p-4 sm:flex-row sm:items-center">
              <div className="text-foreground flex w-full flex-col gap-2">
                <span className="font-medium">Delete this store</span>
                <div className="text-muted-foreground text-sm">
                  Deleting this store will remove all data associated with this
                  store. This action cannot be undone.
                </div>
              </div>
              <div className="flex h-full w-full items-end justify-end sm:w-fit">
                <Button
                  variant="destructive"
                  onClick={() => setDeleteStoreOpen(true)}
                >
                  Delete Store
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      <Dialog open={deleteStoreOpen} onOpenChange={setDeleteStoreOpen}>
        <DialogContent>
          <DialogTitle>Delete Store</DialogTitle>
          <DialogDescription>
            Are you sure you want to delete <strong>{store.description}</strong>
            ? This action cannot be undone and will remove all data associated
            with this store.
          </DialogDescription>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDeleteStoreOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteStore}
              disabled={loading}
            >
              {loading && <Loader2 className="size-4 animate-spin" />}
              Delete Store
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={leaveStoreOpen} onOpenChange={setLeaveStoreOpen}>
        <DialogContent>
          <DialogTitle>Leave Store</DialogTitle>
          <DialogDescription>
            Are you sure you want to leave <strong>{store.description}</strong>?
            You can only rejoin if you are invited again.
          </DialogDescription>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setLeaveStoreOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleLeaveStore}
              disabled={loading}
            >
              {loading && <Loader2 className="size-4 animate-spin" />}
              Leave Store
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
